import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Box, Heading, Text, VStack, Button, Badge, useColorMode } from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredPermissions?: string[];
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  requiredPermissions = [] 
}) => {
  const { isAuthenticated, user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { colorMode } = useColorMode();
  const isDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

  // Wait for auth to load
  if (loading) {
    return (
      <Box minH="100vh" display="flex" alignItems="center" justifyContent="center">
        <Text>Loading...</Text>
      </Box>
    );
  }

  // Check if user is authenticated
  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  // Check if user has required permissions
  if (requiredPermissions.length > 0) {
    const hasPermission = requiredPermissions.some(permission => 
      user?.permissions?.includes(permission)
    );

    if (!hasPermission) {
      return (
        <Box minH="100vh" display="flex" alignItems="center" justifyContent="center" bg="gray.50">
          <VStack spacing={4} textAlign="center" p={8}>
            <Heading size="lg" color="red.500">Access Denied</Heading>
            <Text color="gray.600">
              You don't have permission to access this page.
            </Text>
            <Text fontSize="sm" color="gray.500">
              Required permissions: {requiredPermissions.join(', ')}
            </Text>
            <Button 
              colorScheme="blue" 
              onClick={() => navigate('/')}
              mt={4}
            >
              Go to Home
            </Button>
          </VStack>
        </Box>
      );
    }
  }

  return (
    <>
      {children}
      {isDev && requiredPermissions.length > 0 && (
        <Box
          position="fixed"
          bottom={4}
          right={4}
          bg={colorMode === 'light' ? 'gray.800' : 'gray.700'}
          color="white"
          p={3}
          borderRadius="md"
          fontSize="xs"
          opacity={0.8}
          maxW="280px"
          zIndex={9999}
          boxShadow="lg"
          _hover={{ opacity: 1 }}
        >
          <VStack align="start" spacing={1}>
            <Text fontWeight="bold" fontSize="xs" color="yellow.300">
              DEV: {location.pathname}
            </Text>
            <Text fontSize="xs" color="gray.300">Required permissions:</Text>
            <Box display="flex" flexWrap="wrap" gap={1}>
              {requiredPermissions.map((p) => {
                const hasIt = user?.permissions?.includes(p);
                return (
                  <Badge key={p} colorScheme={hasIt ? 'green' : 'red'} fontSize="8px" variant="subtle">
                    {hasIt ? '✓' : '✗'} {p}
                  </Badge>
                );
              })}
            </Box>
          </VStack>
        </Box>
      )}
    </>
  );
};